import {
  type GetWorkerEventsForTierResponse,
  type TimeSlot,
  type WorkerEventDto,
} from "@clipboard-health/contract-worker-app-bff";
import { startOfDay } from "@clipboard-health/date-time";
import { type EnabledTiersConfig } from "@clipboard-health/flag-attendance-policy";
import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { useGetWorkerEventsForTier } from "@src/appV2/redesign/Tiers/api/useGetWorkerEventsForTier";
import { isArrivalOnTime } from "@src/appV2/redesign/Tiers/utils/isArrivalOnTime";
import { type RatingType } from "@src/appV2/redesign/Tiers/utils/types";
import { DEFAULT_TIMEZONE } from "@src/appV2/Shifts/Shift/constants";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { parseISO } from "date-fns";
import { format } from "date-fns-tz";

export interface WorkerEventForRating {
  id: string;
  isSuccess: boolean;
  eventDescription: string;
  shiftDescription: string;
  shiftTimeSlot: TimeSlot;
  facilityName: string;
  eventDate: Date;
}

interface UseWorkerEventsForRatingResponse {
  isLoading: boolean;
  workerEvents: WorkerEventForRating[];
}

type ShiftAttributes = GetWorkerEventsForTierResponse["included"][number]["attributes"];

// eslint-disable-next-line consistent-return
function buildWorkerEventForRating(parameters: {
  event: WorkerEventDto;
  shift: ShiftAttributes;
  workerTimeZone: string;
  ratingType: RatingType;
  tierConfiguration: Pick<
    EnabledTiersConfig,
    | "lateForgivenessBookingLeadTimeThresholdInMinutes"
    | "lateForgivenessIfAfterAnotherShift"
    | "lateForgivenessGracePeriodInMinutes"
  >;
}): WorkerEventForRating {
  const { event, shift, workerTimeZone, ratingType, tierConfiguration } = parameters;
  // eslint-disable-next-line default-case
  switch (ratingType) {
    case "onTimeArrival": {
      const baseEvent = {
        id: event.id,
        shiftDescription: `${shift.slot.toLocaleUpperCase()} shift`,
        shiftTimeSlot: shift.slot,
        facilityName: shift.facilityName,
        eventDate: startOfDay(parseISO(shift.start), {
          timeZone: workerTimeZone,
        }),
      };
      if (event.attributes.eventType !== "SHIFT_ARRIVAL") {
        return { ...baseEvent, eventDescription: "N/A", isSuccess: false };
      }

      const { attributes: arrivalEvent } = event;
      const isOnTime = isArrivalOnTime(arrivalEvent, tierConfiguration);
      return {
        ...baseEvent,
        isSuccess: isOnTime,
        eventDescription: isOnTime ? "On time" : "Late",
      };
    }

    case "shiftCompletionWithoutCancel":
    case "shiftCompletionWithoutLateCancel": {
      const shiftDate = format(parseISO(shift.start), "EEE, MMM d", { timeZone: workerTimeZone });
      const isSuccess = event.attributes.eventType === "SHIFT_COMPLETION";
      const cancelledDescription =
        ratingType === "shiftCompletionWithoutLateCancel" ? "Cancelled late" : "Cancelled";
      // For cancelled events, use the time the worker cancelled the shift.
      // For worked events, use the shift start time.
      const dateToRecord = isSuccess ? shift.start : event.attributes.occurredAt;
      const eventDate = startOfDay(parseISO(dateToRecord), {
        timeZone: workerTimeZone,
      });
      const eventDescriptionSuffix = isSuccess ? "" : ` for ${shiftDate}`;
      return {
        id: event.id,
        isSuccess,
        eventDescription: isSuccess ? "Worked" : cancelledDescription,
        shiftDescription: `${shift.slot.toLocaleUpperCase()} shift${eventDescriptionSuffix}`,
        shiftTimeSlot: shift.slot,
        facilityName: shift.facilityName,
        eventDate,
      };
    }
  }
}

export function useWorkerEventsForRating(ratingType: RatingType): UseWorkerEventsForRatingResponse {
  const worker = useDefinedWorker();
  const tierConfiguration = useCbhFlag(CbhFeatureFlag.CONFIG_TIERS, {
    defaultValue: { enabled: false },
  });
  const { enabled } = tierConfiguration;
  const {
    data: workerEventsForTier,
    isSuccess,
    isLoading,
  } = useGetWorkerEventsForTier(
    { workerId: worker.userId, rating: ratingType },
    { enabled: isDefined(worker.userId) && enabled }
  );
  if (!enabled) {
    return { isLoading: false, workerEvents: [] };
  }

  if (!isSuccess) {
    return { isLoading, workerEvents: [] };
  }

  const shiftMap = new Map<string, ShiftAttributes>();
  workerEventsForTier.included.forEach((shift) => {
    shiftMap.set(shift.id, shift.attributes);
  });

  const workerEvents = workerEventsForTier.data.map((event: WorkerEventDto) => {
    const shiftId = event.relationships.shift.data.id;
    const shift = shiftMap.get(shiftId);
    if (!shift) {
      throw new Error(`Shift ${shiftId} not found`);
    }

    return buildWorkerEventForRating({
      event,
      shift,
      workerTimeZone: worker.tmz ?? DEFAULT_TIMEZONE,
      ratingType,
      tierConfiguration,
    });
  });
  return { isLoading, workerEvents };
}
